import type { Hit } from 'instantsearch.js';
import { useEffect, useState } from 'react';
import { useInfiniteHits } from 'react-instantsearch-core';

import { Flex } from '@/components/Flex';
import { ProductListingCard } from '@/components/ProductListingCard';
import type { SearchViewType } from '@/components/Search/types';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { isStrapHit, sendAlgoliaEvent } from '@/integrations/algolia/helpers';
import type { StrapHit, WatchHit } from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { HITS_PER_PAGE } from '@/lib/const';
import { useAlgoliaEvent } from '@/providers/AlgoliaEventProvider';
import { MoreResultsButton } from '@/views/SearchView/MoreResultsButton';

import { getProductTypeByActiveTab } from './helper';

import * as S from './ProductsList.styles';

interface ProductsListProps {
  activeTab?: SearchViewType;
  data: Hit<WatchHit | StrapHit>[] | false;
  hitType?: SearchViewType;
  initialResults?: any;
  isOverlay?: boolean;
  itemCount?: number;
  nbHits?: number;
  preview?: boolean;
}

export const ProductsList = ({
  activeTab,
  data,
  hitType,
  initialResults,
  isOverlay = false,
  itemCount,
  nbHits,
  preview = false,
}: ProductsListProps) => {
  const { hits, results } = useInfiniteHits<WatchHit | StrapHit>();
  const finalHits = data ? data : hits;
  let finalHitsFiltered = finalHits;
  const { sendViewItemListEvent } = useGoogleAnalytics();
  const [sentNbHits, setSentNbHits] = useState(0);
  const queryID = results?.queryID ?? '';

  const { setIndexName, setIsSearched, setQueryID } = useAlgoliaEvent();

  const productType = getProductTypeByActiveTab(activeTab);

  if (productType) {
    finalHitsFiltered = finalHits.filter((x) => x.product_type == productType);
  }

  useEffect(() => {
    if (finalHits.length > 0) {
      const skus = finalHits
        .slice(sentNbHits, finalHits.length)
        .map((hit) => hit.objectID);
      if (skus.length > 0)
        sendViewItemListEvent({
          skus,
          itemListName: 'Search product list',
        });
      setSentNbHits(finalHits.length);

      setIsSearched(true);
      setQueryID(queryID);
      setIndexName(results?.index ?? '');
    }
  }, [finalHits, sentNbHits]);

  return (
    <S.Wrapper backgroundColor='white' data-test-id='ProductsListWrapper'>
      {finalHits.length > 0 && (
        <Flex
          data-test-id='ProductsListFlex'
          flexWrap='wrap'
          justifyContent='center'
        >
          <S.HitsWrapper
            col={5}
            data-test-id='ProductsListHitsWrapper'
            itemCount={itemCount ?? 0}
          >
            {finalHits
              .slice(0, preview ? HITS_PER_PAGE : undefined)
              .map((product, index) => {
                let strapSizesDatas;
                if (isStrapHit(product)) {
                  strapSizesDatas = {
                    variant_group: [product.objectID],
                    variants: {
                      [product.objectID]: {
                        strap_length: product.strap_length,
                        call_to_action: product.call_to_action,
                        variant_id: product?.variant_id,
                      },
                    },
                  };
                }
                return (
                  <ProductListingCard
                    key={product.objectID}
                    cardClickHandler={() => {
                      sendAlgoliaEvent({
                        event: AlgoliaEventsNames.clickedObjectIDsAfterSearch,
                        objectIDs: [product.objectID],
                        queryID: queryID,
                        indexName: results?.index,
                        positions: [index + 1],
                      });
                    }}
                    data-test-id={`ListingHitsProductListingCard-${index}`}
                    hit={product}
                    isMostRelevantCta={false}
                    productCatalogModeCtaStyle='HIDDEN'
                    strapSizeDatas={strapSizesDatas}
                  />
                );
              })}
          </S.HitsWrapper>

          <MoreResultsButton
            data-test-id='ProductsListMoreResultsButton'
            finalHits={finalHitsFiltered}
            hitType={hitType}
            initialResults={initialResults}
            isOverlay={isOverlay}
            nbHits={nbHits ?? 0}
          />
        </Flex>
      )}
    </S.Wrapper>
  );
};

ProductsList.displayName = 'ProductList';
